import React from "react";
import { FaCheck, FaClock, FaTimes } from "../../utils/icons";
import type { UploadPhase } from "./types";

/**
 * Persistent pipeline tracker for the new-upload flow (LU-A10 follow-up, approved
 * by Andrea 2026-06-10). Sits at the TOP of the right pane, above the per-phase
 * detail panel (dropzone / parsing / PHI review / progress), and stays mounted as
 * the upload advances so the user always sees where they are.
 *
 * Two zones, fixed height so it never jumps between steps:
 *   - LEFT  : step-specific copy (idle = expected structure; then what-we-check +
 *             disclaimer, an upload timing heads-up, model details, a short ready).
 *             Text only — no progress bars (the detail panel below owns live %).
 *   - RIGHT : the full 4-step pipeline (Check → Upload → Analyze → Review), always
 *             visible, with the active step filled and completed steps tinted/checked.
 *
 * It is the SINGLE source of pipeline truth for the new-upload flow — the standalone
 * `ProcessingPipeline` rail is hidden (showPipeline=false) when this box is mounted.
 * Purely presentational; the page maps its phase machine onto `phase`.
 */
interface UploadPipelineBoxProps {
  /** New-upload phase. `error` shows a halted pipeline + a recovery message. */
  phase: UploadPhase;
  /** Size cap surfaced in the idle structure note. */
  maxMb?: number;
  /** When the user opted to strip identifiers, tailor the upload copy. */
  stripping?: boolean;
  /**
   * Index of the step that failed (0=Check, 1=Upload, …). Only meaningful when
   * `phase === "error"`: that step lights red, earlier steps stay done, later
   * steps stay dim.
   */
  failedStep?: number;
}

const STEPS = [
  { key: "check", label: "Check", sub: "validate & scan" },
  { key: "upload", label: "Upload", sub: "secure" },
  { key: "analyze", label: "Analyze", sub: "AI reads it" },
  { key: "review", label: "Review", sub: "private to you" },
] as const;

/** Phase → index of the active pipeline step (−1 = idle/error, nothing lit). */
function activeIndex(phase: UploadPipelineBoxProps["phase"]): number {
  switch (phase) {
    case "parsing":
    case "needs-ack":
      return 0; // Check
    case "uploading":
      return 1; // Upload
    case "processing":
      return 2; // Analyze
    case "ready":
      return 3; // Review
    case "idle":
    case "error":
    default:
      return -1; // nothing lit (idle = not started, error = halted)
  }
}

/** Small kicker title with an accent square — reads as a heading. Warn tone for errors. */
const StepTitle: React.FC<{
  children: React.ReactNode;
  tone?: "signal" | "warn";
  /** Pulse the accent dot — signals work is actively in progress (the Analyzing step). */
  pulse?: boolean;
}> = ({ children, tone = "signal", pulse = false }) => (
  <h3 className="mb-2.5 flex items-center gap-2 text-[14px] font-semibold leading-none tracking-tight text-fg">
    <span
      className={`h-1.5 w-1.5 flex-none rounded-[2px] ${tone === "warn" ? "bg-warn" : "bg-signal"} ${
        pulse ? "animate-pulse" : ""
      }`}
    />
    {children}
  </h3>
);

const LeftContent: React.FC<UploadPipelineBoxProps> = ({ phase, maxMb = 1024, stripping }) => {
  if (phase === "idle") {
    return (
      <>
        <StepTitle>Expected structure</StepTitle>
        <div className="text-[12px] leading-relaxed text-fg-muted">
          <p className="mb-2">
            Bring <span className="font-medium text-fg">one CT study</span> — a folder of{" "}
            <span className="font-mono">.dcm</span> files. Multiple series sub-folders are fine.
          </p>
          <div className="font-mono text-[11.5px] leading-[1.85] text-fg-muted">
            <div className="text-fg">▸ ct-study/</div>
            <div>
              &nbsp;&nbsp;▸ <span className="text-fg/25">series-1/</span>{" "}
              <span className="text-signal-text-tint">slice-001.dcm …</span>
            </div>
            <div>
              &nbsp;&nbsp;▸ <span className="text-fg/25">series-2/</span>{" "}
              <span className="text-signal-text-tint">slice-001.dcm …</span>
            </div>
          </div>
        </div>
        <div className="mt-auto border-t border-border pt-2.5 text-[10px] leading-snug text-fg/25">
          CT only · one study per upload · up to {maxMb} MB.
        </div>
      </>
    );
  }

  if (phase === "parsing" || phase === "needs-ack") {
    return (
      <>
        <StepTitle>Checking your study</StepTitle>
        <div className="text-[12px] leading-relaxed text-fg-muted">
          <p className="mb-1.5">
            We confirm the files and flag anything that needs your attention:
          </p>
          <ul className="flex flex-col gap-1">
            <li className="flex gap-2 text-[11.5px]">
              <span className="font-semibold text-signal-text-tint">›</span>
              Valid CT DICOM images, single study
            </li>
            <li className="flex gap-2 text-[11.5px]">
              <span className="font-semibold text-signal-text-tint">›</span>
              Patient identifiers — name, MRN, DOB
            </li>
          </ul>
        </div>
        <div className="mt-auto border-t border-border pt-2.5 text-[10px] leading-snug text-fg/25">
          a2z is not responsible for [xyz — disclaimer TBD]. You confirm you have the right to
          upload this study.
        </div>
      </>
    );
  }

  if (phase === "uploading") {
    return (
      <>
        <StepTitle>Uploading securely</StepTitle>
        <div className="text-[12px] leading-relaxed text-fg-muted">
          <p className="mb-2">Your study is being sent over an encrypted connection.</p>
          <p className="flex gap-2 text-fg-muted">
            <FaClock className="mt-0.5 h-3 w-3 flex-none text-signal-text-tint" />
            <span>
              This can take a few moments for large studies — please{" "}
              <span className="font-medium text-fg">keep this tab open</span> until it finishes.
            </span>
          </p>
        </div>
        {stripping && (
          <div className="mt-auto border-t border-border pt-2.5 text-[10px] leading-snug text-fg/25">
            Identifying tags are blanked on your device before upload.
          </div>
        )}
      </>
    );
  }

  if (phase === "processing") {
    return (
      <>
        <StepTitle pulse>Analyzing</StepTitle>
        <div className="text-[12px] leading-relaxed text-fg-muted">
          <span className="mb-2 inline-block rounded-[3px] border border-signal-stroke-soft bg-signal-ambient px-1.5 py-0.5 font-mono text-[11px] text-signal-text-tint">
            a2z-Soma · whole-body CT
          </span>
          <p>
            A whole-body CT model that reads every organ system — lungs, liver, kidneys, vessels,
            bone, and more — and flags what stands out for your review.
          </p>
        </div>
        <div className="mt-auto border-t border-border pt-2.5 text-[10px] leading-snug text-fg/25">
          Analysis may take up to a few minutes. Results are advisory and not a diagnosis.
        </div>
      </>
    );
  }

  if (phase === "error") {
    return (
      <>
        <StepTitle tone="warn">Upload stopped</StepTitle>
        <div className="text-[12px] leading-relaxed text-fg-muted">
          <p>
            We hit a problem and stopped —{" "}
            <span className="font-medium text-fg">nothing was uploaded</span>, your study is
            unchanged.
          </p>
        </div>
        <div className="mt-auto border-t border-border pt-2.5 text-[10px] leading-snug text-fg/25">
          See the details below, then retry or pick a different study.
        </div>
      </>
    );
  }

  // ready
  return (
    <>
      <StepTitle>Ready</StepTitle>
      <div className="text-[12px] leading-relaxed text-fg-muted">
        <p>
          <span className="font-medium text-fg">Your study is ready.</span> It’s opening in your
          uploads list now — private to you.
        </p>
      </div>
    </>
  );
};

export const UploadPipelineBox: React.FC<UploadPipelineBoxProps> = ({
  phase,
  maxMb = 1024,
  stripping = false,
  failedStep,
}) => {
  const active = activeIndex(phase);
  // On error, the failed step lights red; steps before it stayed done.
  const isError = phase === "error";
  const failedIdx = isError && failedStep != null ? failedStep : -1;
  return (
    <div
      data-testid="upload-pipeline-box"
      className="grid h-[176px] grid-cols-[minmax(0,0.92fr)_1.08fr] overflow-hidden rounded border border-border bg-surface"
    >
      {/* LEFT — step copy */}
      <div className="flex min-w-0 flex-col border-r border-border px-4 py-3.5">
        <LeftContent phase={phase} maxMb={maxMb} stripping={stripping} />
      </div>

      {/* RIGHT — full pipeline, always visible */}
      <div className="flex items-center px-5 py-4">
        <ol className="flex w-full items-start">
          {STEPS.map((step, i) => {
            const isFailed = isError && i === failedIdx;
            const done = isError ? i < failedIdx : active > i;
            const isActive = !isError && active === i;
            const lit = done || isActive || isFailed;
            const isLast = i === STEPS.length - 1;
            return (
              <li key={step.key} className="relative flex-1 px-0.5 text-center">
                {/* connector to the next node — solid only between completed steps */}
                {!isLast && (
                  <span
                    className={[
                      "absolute top-[13px] h-px",
                      "left-[calc(50%+15px)] w-[calc(100%-30px)]",
                      done || isActive
                        ? "border-t border-solid border-signal-stroke"
                        : "border-t border-dashed border-border-strong",
                    ].join(" ")}
                  />
                )}
                <span
                  className={[
                    "relative z-1 mx-auto mb-2 grid h-[27px] w-[27px] place-items-center rounded-[3px] border font-mono text-[11px] transition-colors",
                    isFailed
                      ? "border-warn bg-warn-tint text-warn"
                      : isActive
                        ? "border-signal bg-signal text-white shadow-[0_0_0_3px_var(--color-signal-ambient)]"
                        : done
                          ? "border-signal-stroke bg-signal-tint text-signal-text-tint"
                          : "border-border-strong bg-surface-2 text-fg-dim",
                  ].join(" ")}
                >
                  {isFailed ? (
                    <FaTimes className="h-3 w-3" />
                  ) : done ? (
                    <FaCheck className="h-3 w-3" />
                  ) : (
                    i + 1
                  )}
                </span>
                <span
                  className={[
                    "block text-[11.5px] font-semibold leading-tight",
                    isFailed ? "text-warn" : isActive ? "text-signal-text-tint" : "text-fg-muted",
                    !lit ? "opacity-50" : "",
                  ].join(" ")}
                >
                  {step.label}
                </span>
                <span
                  className={[
                    "mt-0.5 block text-[9.5px] leading-tight text-fg/25",
                    !lit ? "opacity-50" : "",
                  ].join(" ")}
                >
                  {step.sub}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};
